import { useEffect, useId, useState } from 'react'
import toast from 'react-hot-toast'
import {
  BeakerIcon,
  PlusIcon,
  XMarkIcon,
  PlayIcon,
  StopIcon,
  ChartBarIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline'
import { abTestingAPI, contentAPI } from '../services/api'
import type { ABTest, ABTestMetric, ABTestReport, Content } from '../types'
import LoadingScreen from '../components/LoadingScreen'
import Tooltip from '../components/Tooltip'
import { AB_TESTING_GLOSSARY } from '../config/glossary'

const METRIC_LABELS: Record<ABTestMetric, string> = {
  engagement_rate: 'Engagement Rate',
  likes: 'Likes',
  comments: 'Comments',
  shares: 'Shares',
}

const STATUS_CONFIG: Record<ABTest['status'], { label: string; classes: string }> = {
  draft: { label: 'Draft', classes: 'bg-gray-100 text-gray-600' },
  running: { label: 'Running', classes: 'bg-primary-50 text-primary-700' },
  completed: { label: 'Completed', classes: 'bg-success-50 text-success-700' },
  cancelled: { label: 'Cancelled', classes: 'bg-gray-100 text-gray-500' },
}

interface VariantRow {
  content_id: string
  label: string
  is_control: boolean
}

const emptyVariant = (): VariantRow => ({ content_id: '', label: '', is_control: false })

interface NewTestModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (test: ABTest) => void
  contentOptions: Content[]
}

const NewTestModal = ({ isOpen, onClose, onCreated, contentOptions }: NewTestModalProps) => {
  const titleId = useId()
  const [name, setName] = useState('')
  const [metric, setMetric] = useState<ABTestMetric>('engagement_rate')
  const [significanceThreshold, setSignificanceThreshold] = useState('0.05')
  const [minSampleSize, setMinSampleSize] = useState('100')
  const [autoSelectWinner, setAutoSelectWinner] = useState(false)
  const [variants, setVariants] = useState<VariantRow[]>([emptyVariant(), emptyVariant()])
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose()
    }
    if (isOpen) document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, isSubmitting, onClose])

  const reset = () => {
    setName('')
    setMetric('engagement_rate')
    setSignificanceThreshold('0.05')
    setMinSampleSize('100')
    setAutoSelectWinner(false)
    setVariants([emptyVariant(), emptyVariant()])
  }

  const updateVariant = (index: number, patch: Partial<VariantRow>) => {
    setVariants((prev) => prev.map((v, i) => (i === index ? { ...v, ...patch } : v)))
  }

  const setControl = (index: number) => {
    setVariants((prev) => prev.map((v, i) => ({ ...v, is_control: i === index })))
  }

  const addVariant = () => setVariants((prev) => [...prev, emptyVariant()])

  const removeVariant = (index: number) =>
    setVariants((prev) => prev.filter((_, i) => i !== index))

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Test name is required'); return }
    if (variants.length < 2) { toast.error('Add at least 2 variants'); return }
    if (variants.some((v) => !v.content_id || !v.label.trim())) {
      toast.error('Every variant needs content and a label')
      return
    }

    setIsSubmitting(true)
    try {
      const test = await abTestingAPI.create({
        name: name.trim(),
        metric,
        significance_threshold: parseFloat(significanceThreshold) || 0.05,
        min_sample_size_per_variant: parseInt(minSampleSize, 10) || 100,
        auto_select_winner: autoSelectWinner,
        variants: variants.map((v) => ({
          content_id: parseInt(v.content_id, 10),
          label: v.label.trim(),
          is_control: v.is_control,
        })),
      })
      toast.success('A/B test created')
      onCreated(test)
      reset()
      onClose()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to create test')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id={titleId} className="text-xl font-bold text-gray-900">New A/B Test</h2>
          <button onClick={onClose} disabled={isSubmitting} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Test Name *</label>
            <input
              type="text"
              placeholder="e.g. New headline vs. old headline"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Metric</label>
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value as ABTestMetric)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {(Object.entries(METRIC_LABELS) as [ABTestMetric, string][]).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Significance (alpha)</label>
              <input
                type="number"
                min="0.01"
                max="0.5"
                step="0.01"
                value={significanceThreshold}
                onChange={(e) => setSignificanceThreshold(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min sample/variant</label>
              <input
                type="number"
                min="1"
                step="1"
                value={minSampleSize}
                onChange={(e) => setMinSampleSize(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={autoSelectWinner}
              onChange={(e) => setAutoSelectWinner(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Auto-select a winner once significant
          </label>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                <Tooltip content={AB_TESTING_GLOSSARY.variants}>Variants</Tooltip> *
              </span>
              <button
                type="button"
                onClick={addVariant}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                + Add variant
              </button>
            </div>
            <div className="space-y-3">
              {variants.map((variant, index) => (
                <div key={index} className="flex items-start gap-2">
                  <select
                    value={variant.content_id}
                    onChange={(e) => updateVariant(index, { content_id: e.target.value })}
                    className="flex-1 px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select content...</option>
                    {contentOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title || c.text.slice(0, 40)}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Label"
                    value={variant.label}
                    onChange={(e) => updateVariant(index, { label: e.target.value })}
                    className="w-24 px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap pt-2">
                    <input
                      type="radio"
                      name="control-variant"
                      checked={variant.is_control}
                      onChange={() => setControl(index)}
                    />
                    Control
                  </label>
                  {variants.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeVariant(index)}
                      className="text-gray-400 hover:text-danger-600 pt-2"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Test'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ReportPanelProps {
  report: ABTestReport
}

const ReportPanel = ({ report }: ReportPanelProps) => {
  const comparisonByVariant = Object.fromEntries(
    report.significance.comparisons.map((c) => [c.variant_id, c])
  )

  return (
    <div className="mt-4 border-t border-gray-200 pt-4 space-y-3">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="pr-4 pb-2">Variant</th>
              <th className="pr-4 pb-2">Views</th>
              <th className="pr-4 pb-2">Likes</th>
              <th className="pr-4 pb-2">Comments</th>
              <th className="pr-4 pb-2">Shares</th>
              <th className="pr-4 pb-2">Metric Value</th>
              <th className="pr-4 pb-2">p-value</th>
              <th className="pb-2">Significant?</th>
            </tr>
          </thead>
          <tbody>
            {report.variant_performance.map((variant) => {
              const comparison = comparisonByVariant[variant.variant_id]
              return (
                <tr key={variant.variant_id} className="border-t border-gray-100">
                  <td className="pr-4 py-2 font-medium text-gray-800">
                    {variant.label}
                    {variant.is_control && (
                      <span className="ml-1 text-xs text-gray-400">(control)</span>
                    )}
                    {report.winner_variant_id === variant.variant_id && (
                      <TrophyIcon className="inline h-4 w-4 text-yellow-500 ml-1" />
                    )}
                  </td>
                  <td className="pr-4 py-2">{variant.views}</td>
                  <td className="pr-4 py-2">{variant.likes}</td>
                  <td className="pr-4 py-2">{variant.comments}</td>
                  <td className="pr-4 py-2">{variant.shares}</td>
                  <td className="pr-4 py-2">{(variant.metric_value * 100).toFixed(2)}%</td>
                  <td className="pr-4 py-2">
                    {comparison ? comparison.p_value.toFixed(4) : '—'}
                  </td>
                  <td className="py-2">
                    {comparison ? (
                      <span className={comparison.is_significant ? 'text-success-600 font-medium' : 'text-gray-400'}>
                        {comparison.is_significant ? 'Yes' : 'No'}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface TestCardProps {
  test: ABTest
  onUpdated: (test: ABTest) => void
}

const TestCard = ({ test, onUpdated }: TestCardProps) => {
  const [isBusy, setIsBusy] = useState(false)
  const [report, setReport] = useState<ABTestReport | null>(null)
  const [isReportOpen, setIsReportOpen] = useState(false)
  const [isLoadingReport, setIsLoadingReport] = useState(false)

  const statusCfg = STATUS_CONFIG[test.status]

  const handleStart = async () => {
    setIsBusy(true)
    try {
      const updated = await abTestingAPI.start(test.id)
      onUpdated(updated)
      toast.success('Test started')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to start test')
    } finally {
      setIsBusy(false)
    }
  }

  const handleCancel = async () => {
    setIsBusy(true)
    try {
      const updated = await abTestingAPI.cancel(test.id)
      onUpdated(updated)
      toast.success('Test cancelled')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to cancel test')
    } finally {
      setIsBusy(false)
    }
  }

  const handleToggleReport = async () => {
    if (isReportOpen) { setIsReportOpen(false); return }
    setIsReportOpen(true)
    if (report) return
    setIsLoadingReport(true)
    try {
      const data = await abTestingAPI.getReport(test.id)
      setReport(data)
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to load report')
      setIsReportOpen(false)
    } finally {
      setIsLoadingReport(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-bold text-gray-900">{test.name}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusCfg.classes}`}>
              {statusCfg.label}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
            <span>
              <span className="text-gray-400">Metric:</span>{' '}
              {METRIC_LABELS[test.metric]}
            </span>
            <span>
              <span className="text-gray-400">Variants:</span> {test.variants.length}
            </span>
            <span>
              <span className="text-gray-400">Auto-select:</span>{' '}
              {test.auto_select_winner ? 'On' : 'Off'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {test.status === 'draft' && (
            <button
              onClick={handleStart}
              disabled={isBusy}
              title="Start test"
              className="p-2 text-primary-600 hover:bg-primary-50 rounded-md transition-colors disabled:opacity-50"
            >
              <PlayIcon className="h-4 w-4" />
            </button>
          )}
          {(test.status === 'draft' || test.status === 'running') && (
            <button
              onClick={handleCancel}
              disabled={isBusy}
              title="Cancel test"
              className="p-2 text-danger-500 hover:bg-danger-50 rounded-md transition-colors disabled:opacity-50"
            >
              <StopIcon className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={handleToggleReport}
            title="View report"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ChartBarIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isReportOpen && (
        isLoadingReport ? (
          <p className="mt-4 text-sm text-gray-400">Loading report...</p>
        ) : report ? (
          <ReportPanel report={report} />
        ) : null
      )}
    </div>
  )
}

const ABTesting = () => {
  const [tests, setTests] = useState<ABTest[]>([])
  const [contentOptions, setContentOptions] = useState<Content[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    Promise.all([abTestingAPI.list(), contentAPI.list()])
      .then(([testsData, contentData]) => {
        setTests(Array.isArray(testsData) ? testsData : [])
        setContentOptions(Array.isArray(contentData) ? contentData : [])
      })
      .catch(() => toast.error('Failed to load A/B tests'))
      .finally(() => setIsLoading(false))
  }, [])

  const handleCreated = (test: ABTest) => {
    setTests((prev) => [test, ...prev])
  }

  const handleUpdated = (updated: ABTest) => {
    setTests((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
  }

  if (isLoading) return <LoadingScreen />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            <Tooltip content={AB_TESTING_GLOSSARY.abTest}>A/B Testing</Tooltip>
          </h1>
          <p className="text-gray-500 mt-1">
            Compare engagement across content variants and{' '}
            <Tooltip content={AB_TESTING_GLOSSARY.winnerRule}>let the winner reveal itself</Tooltip>
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium"
        >
          <PlusIcon className="h-5 w-5" />
          New Test
        </button>
      </div>

      {tests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BeakerIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">No A/B tests yet</p>
          <p className="text-gray-400 mt-1 mb-5">Create a test to compare engagement across content variants.</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
          >
            Create Test
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {tests.map((test) => (
            <TestCard key={test.id} test={test} onUpdated={handleUpdated} />
          ))}
        </div>
      )}

      <NewTestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={handleCreated}
        contentOptions={contentOptions}
      />
    </div>
  )
}

export default ABTesting
